import { useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useReturnTo } from "@/lib/returnTo";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Loader2, X, Plus,
  Bandage, Thermometer, Hammer, Leaf, FileText, Camera, Search, Car, LocateFixed, Scale,
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useRequireAuth } from "@/lib/auth";
import { useGps } from "@/features/gps/useGps";
import { GpsIndicator } from "@/features/gps/GpsIndicator";
import { Card, Button, Input, Select, Textarea, Spinner, EmptyState, Pill } from "@/components/ui";
import { StickyBottomBar } from "@/components/layout/StickyBottomBar";
import { queueItem } from "@/lib/offline";
import { uploadHseEvidence, type HseEvidencePhoto } from "@/features/hse/evidenceUpload";
import { SignaturePad, type SignaturePadHandle } from "@/features/signature/SignaturePad";
import type { WatermarkContext } from "@/features/photos/capture";
import { VEHICLE_PHOTO_TAGS, MOVEMENT_LABEL } from "@/features/hse/vehicleAccidentShared";
import { AccidentSketch, DEFAULT_ROAD, type AccidentSketchHandle, type SketchItem, type RoadSpec } from "@/features/hse/AccidentSketch";
import { BodySketch, type BodySketchHandle } from "@/features/hse/BodyMap";   // 1 Nov 2026 — circle where it hurts
import { uploadHseDrawing } from "@/features/hse/evidenceUpload";

// =============================================================================
// Incident Report v2 — T-423b (code-defined wizard; the stale seeded template
// is no longer rendered — its row remains only as the form_submissions FK)
// =============================================================================
// James's smoke verdict on v1: "lacklustre... can't pick from employees for
// persons involved... photo bug stops submitting". This version:
//   * Incident TYPE tiles drive a conditional flow (design doc §3.4 PART A)
//   * People picked from employees + subbie workers + free-text visitors
//   * Per-person injury detail (body parts, nature, mechanism, treatment)
//   * Severity DERIVED from treatment level — no jargon select for field crews
//     ("Report only" is its own severity since mig 411 — never a near miss)
//   * Photos go to the hse-evidence bucket (the old pipeline died in
//     /photos/finalise which requires a HOP pack — never worked for forms)
//   * Review step before submit; offline queue + promotion preserved
//
// 1 Nov 2026 — VEHICLE ACCIDENT (mig 482/483). James, after Larry's Hilux was
// hit from behind by a motorbike on the Friday drive to work: "I need to tweak
// Ops so Larry can report it on his phone, and we can send the report to our
// insurance broker." A Vehicle accident tile with its own steps — our vehicle
// (picked from the register), the other party, the conditions, the driver's
// own sequence of events, and photos tagged by what they show — all written
// as facts. His rule: "Coretel should not admit fault through the incident
// form." There is no fault question anywhere on it; what the other party
// said, and any admission by anyone, are recorded as what was said.
// field_values.vehicle → incident_vehicle_details, field_values.timeline →
// the chain of events (both by incident_from_submission, mig 482). Severity
// for a vehicle accident or property damage with nobody hurt is the new
// "Property damage" rung — it is not a near miss.
// Later the same day: James walked the nine steps and wanted it "top to
// bottom" — the Vehicle accident tile now opens the one-page
// VehicleAccidentReportScreen (/forms/run/VEHICLE_ACCIDENT). The vehicle
// steps below are kept for now so nothing cached on a phone breaks; they
// are not reachable from the tile.
// =============================================================================

const TEMPLATE_CODE = "INCIDENT";

type IncidentType = "injury" | "illness" | "near_miss" | "vehicle" | "property_damage" | "environmental" | "report_only";

const TYPE_TILES: { key: IncidentType; label: string; icon: any; hint: string }[] = [
  { key: "injury",          label: "Injury",           icon: Bandage,       hint: "Someone got hurt" },
  { key: "illness",         label: "Illness",          icon: Thermometer,   hint: "Work-related illness" },
  { key: "near_miss",       label: "Near miss",        icon: AlertTriangle, hint: "Could have hurt someone" },
  { key: "vehicle",         label: "Vehicle accident", icon: Car,           hint: "A Coretel vehicle in a crash" },
  { key: "property_damage", label: "Property damage",  icon: Hammer,        hint: "Plant or property" },
  { key: "environmental",   label: "Environmental",    icon: Leaf,          hint: "Spill, dust, noise" },
  { key: "report_only",     label: "Report only",      icon: FileText,      hint: "For the record" },
];

// Vehicle accident — what a photo shows, and the movement words (shared with
// the incident page and the broker report).
const MOVEMENTS: Array<[string, string]> = Object.entries(MOVEMENT_LABEL);
const ROADS    = ["Dry", "Wet", "Gravel / unsealed", "Roadworks", "Other"];
const WEATHERS = ["Fine", "Rain", "Fog", "Wind", "Other"];
const LIGHTS   = ["Daylight", "Dawn / dusk", "Dark — street lit", "Dark — unlit"];

type ThirdParty = {
  name: string; phone: string; email: string; address: string;
  rego: string; vehicle: string; insurer: string; policy_ref: string;
  damage: string; towed: boolean | null; injury_reported: boolean | null; injury_detail: string;
};
const emptyThirdParty = (): ThirdParty => ({ name: "", phone: "", email: "", address: "", rego: "", vehicle: "", insurer: "", policy_ref: "", damage: "", towed: null, injury_reported: null, injury_detail: "" });
type TimelineRow = { time: string; entry: string };

const BODY_PARTS = ["Head", "Face", "Eye", "Neck", "Shoulder", "Arm", "Elbow", "Wrist", "Hand", "Fingers", "Back", "Chest", "Abdomen", "Hip", "Leg", "Knee", "Ankle", "Foot", "Internal", "Multiple"];
const NATURES = ["Laceration / cut", "Fracture", "Sprain / strain", "Burn", "Electric shock", "Crush", "Amputation", "Foreign body", "Bruise / contusion", "Dislocation", "Other"];
const MECHANISMS = ["Struck by object", "Struck against", "Caught between", "Fall from height", "Slip / trip / fall", "Manual handling", "Electrical contact", "Exposure to substance", "Plant / equipment", "Other"];
const TREATMENTS = [
  { key: "none",      label: "No treatment needed" },
  { key: "first_aid", label: "First aid on site" },
  { key: "doctor",    label: "Doctor / GP" },
  { key: "hospital",  label: "Hospital (emergency dept)" },
  { key: "admitted",  label: "Admitted to hospital" },
] as const;

type Person = {
  name: string;
  company: string;
  source: "employee" | "subbie" | "other";
  employee_id?: string;
  worker_id?: string;
  injury?: { body_parts: string[]; nature: string; mechanism: string; treatment: string; sketch_strokes?: Array<Array<[number, number]>>; sketch_path?: string | null };   // sketch_* 1 Nov 2026 — the body outline they circled
};

// Mig 411 (3 Oct 2026): "Report only" is its own rung at the bottom of the
// ladder. James: "I tried to raise a report incident, the one for just
// reporting, but it's coming up as a near miss". A for-the-record note is not
// a near miss and must never be counted as one.
// Mig 482 (1 Nov 2026): "Property damage" is its own rung too — a vehicle
// accident or property damage with nobody hurt is not a near miss, something
// got damaged. If someone WAS hurt in a vehicle accident the injury ladder
// takes over, same as an injury.
// Mig 485 (1 Nov 2026): "Environmental" is its own rung — James: "add a rung on
// the ladder for environmental and not just under near miss." A spill, dust or
// noise event with nobody hurt is an environmental incident, not a near miss.
const SEV_RANK = ["Report only", "Near miss", "Property damage", "Environmental", "First aid", "Medical treatment", "Lost time", "Serious", "Notifiable", "Fatality"];
function maxSev(a: string, b: string) { return SEV_RANK.indexOf(a) >= SEV_RANK.indexOf(b) ? a : b; }

function deriveSeverity(type: IncidentType, persons: Person[], escalation: string, lostTime: boolean, anyoneHurt = false): string {
  if (escalation === "fatality") return "Fatality";
  if (escalation === "life_threatening") return "Notifiable";
  if (type === "report_only") return "Report only";
  if (type === "property_damage") return "Property damage";
  if (type === "environmental") return "Environmental";
  if (type === "vehicle" && !anyoneHurt) return "Property damage";
  if (type !== "injury" && type !== "illness" && type !== "vehicle") return "Near miss";
  let sev = "First aid";
  for (const p of persons) {
    const t = p.injury?.treatment ?? "none";
    if (t === "doctor")   sev = maxSev(sev, "Medical treatment");
    if (t === "hospital") sev = maxSev(sev, "Serious");
    if (t === "admitted") sev = maxSev(sev, "Notifiable");
  }
  if (lostTime) sev = maxSev(sev, "Lost time");
  return sev;
}

export default function IncidentReportScreen() {
  const user = useRequireAuth();
  const navigate = useNavigate();
  const gps = useGps(true);
  const sigRef = useRef<SignaturePadHandle | null>(null);

  // Client-minted so photos can key their storage prefix BEFORE submit, and
  // so offline replay + promotion are idempotent end-to-end.
  const [submissionId] = useState(() => crypto.randomUUID());

  const [step, setStep] = useState(0);
  const [err, setErr] = useState<string | null>(null);

  // Step 1 — what happened
  const [type, setType] = useState<IncidentType | null>(null);
  // mig 392 — arriving from a job's Site folder: ?project=<id> preselects it.
  const [searchParams] = useSearchParams();
  const returnTo = useReturnTo();   // 27 Sep — opened from a Site folder? Done/back go there
  const [projectId, setProjectId] = useState<string>(searchParams.get("project") ?? "");
  const [occDate, setOccDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [occTime, setOccTime] = useState(() => {
    const d = new Date(Date.now() - 5 * 60000);
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });
  const [shortDesc, setShortDesc] = useState("");
  const [fullDesc, setFullDesc] = useState("");

  // Step 2 — people
  const [persons, setPersons] = useState<Person[]>([]);
  const bodyRefs = useRef<Record<number, BodySketchHandle | null>>({});   // 1 Nov 2026 — one body sketch per injured person
  const [witnesses, setWitnesses] = useState<Person[]>([]);

  // Step 3 — type-specific
  const [escalation, setEscalation] = useState("none");   // none | life_threatening | fatality
  const [lostTime, setLostTime] = useState(false);
  const [envDetail, setEnvDetail] = useState({ kind: "spill", detail: "", amount: "", surface: "" });
  const [propDetail, setPropDetail] = useState({ coretel: "", third_party: "" });

  // Vehicle accident (mig 482) — our vehicle, the other party, the conditions,
  // the sequence of events. Facts only; there is no fault field by design.
  const [vehicleId, setVehicleId] = useState("");
  const [vehicleText, setVehicleText] = useState("");
  const [driverEmployeeId, setDriverEmployeeId] = useState("");
  const [driverName, setDriverName] = useState("");
  const [movement, setMovement] = useState("");
  const [vehDamage, setVehDamage] = useState("");
  const [drivable, setDrivable] = useState<boolean | null>(null);
  const [towed, setTowed] = useState<boolean | null>(null);
  const [thirdParties, setThirdParties] = useState<ThirdParty[]>([emptyThirdParty()]);
  const [locationText, setLocationText] = useState("");
  const [locationFix, setLocationFix] = useState<{ lat: number; lng: number } | null>(null);
  const [cond, setCond] = useState({ road: "", weather: "", light: "", speed_zone: "", lane_detail: "", indicators: "" });
  const [tpStatement, setTpStatement] = useState("");
  const [admissions, setAdmissions] = useState("");
  const [police, setPolice] = useState<{ attended: boolean | null; event_number: string; station: string; emergency: string }>({ attended: null, event_number: "", station: "", emergency: "" });
  const [timeline, setTimeline] = useState<TimelineRow[]>([{ time: "", entry: "" }]);
  const [anyoneHurt, setAnyoneHurt] = useState(false);
  const [photoTag, setPhotoTag] = useState("coretel_vehicle");
  // The sketch — James, 1 Nov 2026: "I won't accept any incident without the
  // scribe / draw it down as the insurance need it." Required; exported to a
  // PNG and uploaded to hse-evidence when the person leaves the step.
  const [sketchItems, setSketchItems] = useState<SketchItem[]>([]);
  const [sketchRoad, setSketchRoad] = useState<RoadSpec>(DEFAULT_ROAD);
  const [sketchPath, setSketchPath] = useState<string | null>(null);
  const [sketchDirty, setSketchDirty] = useState(false);
  const [sketchBusy, setSketchBusy] = useState(false);
  const sketchRef = useRef<AccidentSketchHandle | null>(null);

  // Step 4 — actions + evidence
  const [immediate, setImmediate] = useState("");
  const [authorityNotice, setAuthorityNotice] = useState("None");
  const [photos, setPhotos] = useState<HseEvidencePhoto[]>([]);
  const [noPhotosOk, setNoPhotosOk] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoErr, setPhotoErr] = useState<string | null>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const [result, setResult] = useState<{ submission_number?: string; incident_number?: string; offline: boolean } | null>(null);

  const { data: template, isLoading } = useQuery({
    queryKey: ["form_template", TEMPLATE_CODE],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("form_templates")
        .select("template_id, display_name")
        .eq("template_code", TEMPLATE_CODE).eq("is_active", true)
        .order("version", { ascending: false }).limit(1).maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  const { data: projects = [] } = useQuery({
    queryKey: ["incident_projects"],
    queryFn: async () => {
      const { data } = await supabase
        .from("projects")
        .select("project_id, project_code, project_name, site_id, sites:site_id(site_code, site_name)")
        .in("status", ["scheduled", "won", "mobilising", "live", "on_hold", "acceptance_pending"])
        .order("project_code", { ascending: false });
      return (data ?? []) as any[];
    },
  });
  const project = projects.find(p => p.project_id === projectId) ?? null;

  // The vehicle register (everyone can read it) and the crew list for the
  // driver. Picking a vehicle fills the driver from its allocation; the
  // reporter is the fallback.
  const { data: vehicles = [] } = useQuery({
    queryKey: ["incident_vehicles"],
    queryFn: async () => {
      const { data } = await (supabase as any).from("vehicles")
        .select("vehicle_id, vehicle_code, rego, make, model, assigned_to_employee_id, allocation_label")
        .eq("is_active", true).order("rego");
      return (data ?? []) as { vehicle_id: string; vehicle_code: string; rego: string; make: string | null; model: string | null; assigned_to_employee_id: string | null; allocation_label: string | null }[];
    },
  });
  const { data: crew = [] } = useQuery({
    queryKey: ["picker_employees"],
    queryFn: async () => {
      const { data } = await supabase.from("v_employee_names")
        .select("employee_id, full_name").eq("is_active", true).order("full_name");
      return (data ?? []) as { employee_id: string; full_name: string }[];
    },
  });
  const reporterName = user ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() : "";
  function pickVehicle(id: string) {
    setVehicleId(id);
    const v = vehicles.find(x => x.vehicle_id === id);
    const emp = v?.assigned_to_employee_id ?? user?.employee_id ?? "";
    setDriverEmployeeId(emp);
    setDriverName(crew.find(c => c.employee_id === emp)?.full_name ?? (emp === user?.employee_id ? reporterName : ""));
  }

  const wmCtx: WatermarkContext = useMemo(() => ({
    project_code: project?.project_code,
    site_code: project?.sites?.site_code,
    employee_name: user ? `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() || user.login_email : undefined,
  }), [user, project]);

  const isVehicle = type === "vehicle";
  const isInjuryLike = type === "injury" || type === "illness" || (isVehicle && anyoneHurt);
  const hasDetailStep = type != null && type !== "near_miss" && type !== "report_only" && type !== "vehicle";

  // Step list adapts to the type — no dead screens for a near miss. A vehicle
  // accident walks the driver through the facts the broker needs, in order.
  const steps = useMemo(() => {
    if (isVehicle) {
      const s = ["What happened", "Our vehicle", "Other party", "Conditions", "Sketch", "Sequence of events", "Who was involved"];
      if (anyoneHurt) s.push("Injuries");
      s.push("Actions & evidence", "Review & submit");
      return s;
    }
    const s = ["What happened", "Who was involved"];
    if (hasDetailStep) s.push(isInjuryLike ? "Injuries" : "Damage details");
    s.push("Actions & evidence", "Review & submit");
    return s;
  }, [hasDetailStep, isInjuryLike, isVehicle, anyoneHurt]);

  const severityLabel = deriveSeverity(type ?? "report_only", persons, escalation, lostTime, anyoneHurt);
  const likelyNotifiable = SEV_RANK.indexOf(severityLabel) >= SEV_RANK.indexOf("Serious");

  function validateStep(i: number): string | null {
    const name = steps[i];
    if (name === "What happened") {
      if (!type) return "Pick what kind of incident this is.";
      if (!shortDesc.trim()) return "Give it a one-line description.";
      if (!occDate || !occTime) return "When did it happen?";
    }
    if (name === "Our vehicle") {
      if (!vehicleId && !vehicleText.trim()) return "Pick the Coretel vehicle, or type its rego.";
      if (!driverName.trim()) return "Who was driving?";
      if (!movement) return "What was our vehicle doing at the moment of impact?";
      if (drivable == null) return "Could it still be driven afterwards?";
    }
    if (name === "Other party") {
      const real = thirdParties.filter(t => t.name.trim() || t.rego.trim() || t.vehicle.trim());
      if (real.length === 0) return "Add the other vehicle — even just its rego or what it was, if you have no name.";
    }
    if (name === "Conditions") {
      if (!locationText.trim()) return "Where did it happen? Street and suburb is enough.";
      if (police.attended == null) return "Did police attend? Yes or no.";
    }
    if (name === "Sketch") {
      if (sketchItems.length === 0) return "Draw the accident — where each vehicle was and which way it was going. The insurer needs it; the report can't go in without one.";
      if (!sketchItems.some(i => i.kind === "stamp" && i.stamp === "coretel")) return "Put our vehicle on the sketch (tap Our vehicle, then tap the road).";
    }
    if (name === "Sequence of events") {
      const real = timeline.filter(t => t.entry.trim());
      if (real.length === 0) return "Add at least one line — what happened first.";
      if (real.some(t => !t.time)) return "Every line needs a time (your best estimate is fine).";
    }
    if (name === "Who was involved") {
      if (isInjuryLike && persons.length === 0) return isVehicle ? "You ticked someone was hurt — add them." : "Add at least the injured person.";
    }
    if (name === "Injuries") {
      const missing = persons.filter(p => !p.injury?.treatment);
      if (missing.length > 0) return `Set the treatment level for ${missing.map(p => p.name).join(", ")}.`;
    }
    if (name === "Actions & evidence") {
      if (!immediate.trim()) return "What was done immediately? Even 'nothing — area isolated' counts.";
      if (photos.length === 0 && !noPhotosOk) return "Add at least one photo, or tick 'no photos available'.";
    }
    return null;
  }

  // Export the sketch to a PNG and put it in hse-evidence beside the photos
  // (same uploader, so offline it queues and replays to the same path).
  async function saveSketch(): Promise<string | null> {
    if (!sketchRef.current || sketchRef.current.isEmpty()) return null;
    if (sketchPath && !sketchDirty) return sketchPath;
    const blob = await sketchRef.current.exportBlob();
    if (!blob) throw new Error("Couldn't make the sketch image — try again.");
    const file = new File([blob], "accident-sketch.png", { type: "image/png" });
    const res = await uploadHseEvidence(file, wmCtx, gps.fix, `incidents/${submissionId}`);
    setSketchPath(res.path); setSketchDirty(false);
    return res.path;
  }

  async function next() {
    const v = validateStep(step);
    if (v) { setErr(v); return; }
    setErr(null);
    if (steps[step] === "Sketch") {
      setSketchBusy(true);
      try { await saveSketch(); }
      catch (e: any) { setErr(e?.message ?? String(e)); return; }
      finally { setSketchBusy(false); }
    }
    setStep(s => Math.min(steps.length - 1, s + 1));
  }

  async function handlePhotoFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setPhotoBusy(true); setPhotoErr(null);
    try {
      for (const f of Array.from(files)) {
        const res = await uploadHseEvidence(f, wmCtx, gps.fix, `incidents/${submissionId}`);
        // vehicle accident — the photo carries what it shows (mig 482 / the broker report)
        setPhotos(prev => [...prev, isVehicle ? { ...res, tag: photoTag } as HseEvidencePhoto & { tag: string } : res]);
      }
    } catch (e: any) {
      setPhotoErr(e?.message ?? String(e));
    } finally {
      setPhotoBusy(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  }

  const submitMut = useMutation({
    mutationFn: async () => {
      if (!template || !user || !type) throw new Error("not_ready");
      for (let i = 0; i < steps.length; i++) {
        const v = validateStep(i);
        if (v) { setStep(i); setErr(v); throw new Error("validation_failed"); }
      }

      const occurred_at = new Date(`${occDate}T${occTime}:00`).toISOString();
      const sketch_path = type === "vehicle" ? await saveSketch() : null;   // required on a vehicle accident (validated above)
      // 1 Nov 2026 — each injured person's body sketch goes up as a PNG beside the photos (tag "injury")
      const injuryPhotos: Array<Record<string, any>> = [];
      const personsOut: Person[] = [];
      for (let i = 0; i < persons.length; i++) {
        const p = persons[i];
        const h = bodyRefs.current[i];
        if (p.injury && h && !h.isEmpty() && (p.injury.sketch_strokes?.length ?? 0) > 0) {
          const blob = await h.exportBlob();
          if (blob) {
            const res = await uploadHseDrawing(blob, `incidents/${submissionId}`, `injury-${i + 1}.png`);
            injuryPhotos.push({ path: res.path, captured_at: new Date().toISOString(), queued_offline: res.queued_offline, tag: "injury", caption: `${p.name} — where it hurts` });
            personsOut.push({ ...p, injury: { ...p.injury, sketch_path: res.path } });
            continue;
          }
        }
        personsOut.push(p);
      }
      const sig = sigRef.current && !sigRef.current.isEmpty()
        ? { svg: sigRef.current.getSvg(), meta: sigRef.current.getMeta() }
        : null;

      const field_values: Record<string, any> = {
        schema_version: 2,
        incident_type: type,
        severity: severityLabel,                    // same labels the promotion fn maps
        occurred_at,
        short_desc: shortDesc.trim(),
        full_desc: fullDesc.trim() || shortDesc.trim(),
        immediate: immediate.trim(),
        persons_involved: personsOut.map(({ injury, ...p }) => ({ ...p, ...(injury ? { injury: { ...injury, sketch_strokes: undefined } } : {}) })),
        witnesses,
        escalation,
        lost_time_expected: lostTime,
        authority_notice: authorityNotice,
        photos: [...photos.map(({ preview_url, ...p }) => p), ...injuryPhotos],
        no_photos_reason: photos.length === 0 ? "reporter confirmed none available" : undefined,
        ...(type === "environmental" ? { environmental: envDetail } : {}),
        ...(type === "property_damage" ? { property: propDetail } : {}),
        // mig 482 — incident_from_submission writes these two onto
        // incident_vehicle_details and incident_timeline. Booleans stay
        // booleans (or null), never strings — the function type-checks them.
        ...(type === "vehicle" ? {
          vehicle: {
            vehicle_id: vehicleId || null,
            vehicle_text: vehicleText.trim() || (vehicleId ? (() => { const v = vehicles.find(x => x.vehicle_id === vehicleId); return v ? `${v.rego} · ${[v.make, v.model].filter(Boolean).join(" ")}`.trim() : null; })() : null),
            driver_employee_id: driverEmployeeId || null,
            driver_name: driverName.trim() || null,
            drivable, towed,
            damage: vehDamage.trim() || null,
            location_text: locationText.trim() || null,
            location_lat: locationFix?.lat ?? null, location_lng: locationFix?.lng ?? null,
            road: cond.road || null, weather: cond.weather || null, light: cond.light || null,
            speed_zone: cond.speed_zone.trim() || null, lane_detail: cond.lane_detail.trim() || null,
            coretel_movement: movement || null, indicators: cond.indicators.trim() || null,
            third_party_statement: tpStatement.trim() || null, admissions: admissions.trim() || null,
            police_attended: police.attended, police_event_number: police.event_number.trim() || null,
            police_station: police.station.trim() || null, emergency_services: police.emergency.trim() || null,
            sketch_path,
            third_parties: thirdParties.filter(t => t.name.trim() || t.rego.trim() || t.vehicle.trim()).map(t => ({
              ...t, name: t.name.trim(), phone: t.phone.trim(), email: t.email.trim(), address: t.address.trim(), rego: t.rego.trim().toUpperCase(),
              vehicle: t.vehicle.trim(), insurer: t.insurer.trim(), policy_ref: t.policy_ref.trim(), damage: t.damage.trim(), injury_detail: t.injury_detail.trim(),
            })),
          },
          timeline: timeline.filter(t => t.entry.trim() && t.time).map(t => ({
            happened_at: new Date(`${occDate}T${t.time}:00`).toISOString(), entry: t.entry.trim(),
          })),
        } : {}),
        ...(sig ? { reporter_signature: sig } : {}),
      };

      const payload = {
        submission_id: submissionId,
        template_id: template.template_id,
        project_id: projectId || null,
        site_id: project?.site_id ?? null,
        submitted_by_employee_id: user.employee_id,
        submitted_by_user_id: user.user_id,
        field_values,
        captured_at: new Date().toISOString(),
        gps_lat: gps.fix?.latitude,
        gps_lng: gps.fix?.longitude,
        gps_accuracy_m: gps.fix?.accuracy_m,
        device_info: { ua: navigator.userAgent, platform: navigator.platform, online: navigator.onLine },
      };

      if (!navigator.onLine) {
        await queueItem("form_submission", { ...payload, __promote_incident: true });
        return { offline: true };
      }

      const { data: sub, error: e1 } = await supabase
        .from("form_submissions").insert(payload)
        .select("submission_id, submission_number").single();
      if (e1) throw e1;

      const { data: incident_id, error: e2 } = await supabase.rpc("incident_from_submission", { p_submission_id: sub.submission_id });
      if (e2) throw e2;

      let incident_number: string | undefined;
      if (incident_id) {
        const { data: i } = await supabase.from("incidents").select("incident_number").eq("incident_id", incident_id).maybeSingle();
        incident_number = i?.incident_number;
      }
      return { offline: false, submission_number: sub.submission_number, incident_number };
    },
    onSuccess: r => setResult(r),
  });

  if (isLoading) return <div className="py-20 flex justify-center"><Spinner /></div>;
  if (!template) {
    return (
      <div className="max-w-md mx-auto p-6">
        <Card><EmptyState title="Incident form not configured" description="Ask a director to activate the INCIDENT template." icon={AlertTriangle} /></Card>
      </div>
    );
  }

  if (result) {
    return (
      <div className="max-w-md mx-auto p-4 pt-16 text-center">
        <div className={`w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-5 ${result.offline ? "bg-warn/12" : "bg-ok/12"}`}>
          <CheckCircle2 size={40} className={result.offline ? "text-warn" : "text-ok"} />
        </div>
        <h2 className="text-xl font-semibold">Incident reported</h2>
        {result.offline ? (
          <div className="text-text-muted text-[13px] mt-2">
            Saved on this phone. It uploads the moment you're back in coverage —<br/>
            photos included — and directors are notified automatically.
          </div>
        ) : (
          <div className="text-text-muted text-[13px] mt-2 space-y-1">
            <div>Incident <span className="font-mono text-accent">{result.incident_number ?? "—"}</span></div>
            <div className="text-[11px] pt-2">Directors have been alerted.{likelyNotifiable ? " This looks notifiable — they'll see the regulator deadline on the register." : ""}</div>
          </div>
        )}
        <div className="mt-8 flex flex-col gap-2">
          <Button onClick={() => navigate(returnTo ?? "/forms")}>Done</Button>
        </div>
      </div>
    );
  }

  const stepName = steps[step];
  const progress = ((step + 1) / steps.length) * 100;

  return (
    <div className="max-w-lg mx-auto p-4 pb-32">
      <button onClick={() => navigate(returnTo ?? "/forms")} className="text-text-muted text-[12px] inline-flex items-center gap-1.5 mb-3">
        <ArrowLeft size={14}/> {returnTo ? "Site folder" : "Forms"}
      </button>

      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-md bg-danger/12 flex items-center justify-center shrink-0">
          <AlertTriangle size={16} className="text-danger" />
        </div>
        <div className="flex-1">
          <div className="text-[11px] font-mono tracking-wider2 uppercase text-text-muted">Report</div>
          <h1 className="text-[22px] font-semibold tracking-tight">Incident report</h1>
        </div>
        <div className="bg-accent/12 text-accent px-2 py-1 rounded text-[11px] font-mono self-start">
          {step + 1}/{steps.length}
        </div>
      </div>

      <div className="flex items-center gap-3 text-[11px] text-text-muted mb-3">
        <GpsIndicator gps={gps} />
      </div>
      <div className="h-1 bg-ink-raised rounded-full overflow-hidden mb-4">
        <div className="h-full bg-accent transition-[width] duration-300" style={{ width: `${progress}%` }} />
      </div>

      <Card>
        <div className="p-5 space-y-5">
          <div className="text-[15px] font-medium text-text">{stepName}</div>

          {/* Vehicle accident — James, 1 Nov 2026: facts and evidence only;
              fault is for the insurers, brokers and authorities. */}
          {isVehicle && step > 0 && stepName !== "Review & submit" && (
            <div className="flex items-start gap-2 bg-warn/10 border border-warn/30 rounded-md px-3 py-2 text-[12px] text-text">
              <Scale size={14} className="text-warn shrink-0 mt-0.5"/>
              <div><b>Record facts only.</b> Don't say who was at fault or guess — the insurer works that out. Write what you saw and what was said.</div>
            </div>
          )}

          {/* ---- Step: What happened ---- */}
          {stepName === "What happened" && (
            <>
              <div className="grid grid-cols-2 gap-2">
                {TYPE_TILES.map(t => {
                  const Icon = t.icon;
                  const on = type === t.key;
                  return (
                    <button key={t.key} type="button" onClick={() => t.key === "vehicle" ? navigate(`/forms/run/VEHICLE_ACCIDENT${projectId ? `?project=${projectId}` : ""}`) : setType(t.key)}
                      className={`rounded-md border p-3 text-left transition ${on ? "border-accent bg-accent/10" : "border-line hover:border-text-muted"}`}>
                      <Icon size={16} className={on ? "text-accent" : "text-text-muted"}/>
                      <div className={`text-[13px] font-medium mt-1.5 ${on ? "text-accent" : "text-text"}`}>{t.label}</div>
                      <div className="text-[11px] text-text-muted">{t.hint}</div>
                    </button>
                  );
                })}
              </div>
              <label className="block text-[12px] text-text-muted">Project / site
                <Select value={projectId} onChange={e => setProjectId(e.target.value)}>
                  <option value="">Not on a project site</option>
                  {projects.map(p => (
                    <option key={p.project_id} value={p.project_id}>
                      {p.project_code} · {p.sites?.site_name ?? p.project_name}
                    </option>
                  ))}
                </Select>
              </label>
              <div className="flex gap-2">
                <label className="block text-[12px] text-text-muted flex-1">Date
                  <Input type="date" value={occDate} max={new Date().toISOString().slice(0, 10)} onChange={e => setOccDate(e.target.value)}/>
                </label>
                <label className="block text-[12px] text-text-muted flex-1">Time
                  <Input type="time" value={occTime} onChange={e => setOccTime(e.target.value)}/>
                </label>
              </div>
              <label className="block text-[12px] text-text-muted">What happened, in one line
                <Input value={shortDesc} onChange={e => setShortDesc(e.target.value)} placeholder="e.g. Finger caught between winch drum and frame"/>
              </label>
              <label className="block text-[12px] text-text-muted">More detail (optional here — the investigator will follow up)
                <Textarea rows={3} value={fullDesc} onChange={e => setFullDesc(e.target.value)} placeholder="What was being done, what went wrong, conditions at the time…"/>
              </label>
            </>
          )}

          {/* ---- Vehicle accident: Our vehicle ---- */}
          {stepName === "Our vehicle" && (
            <>
              <label className="block text-[12px] text-text-muted">Coretel vehicle <span className="text-danger">*</span>
                <Select value={vehicleId} onChange={e => pickVehicle(e.target.value)}>
                  <option value="">Pick from the register…</option>
                  {vehicles.map(v => (
                    <option key={v.vehicle_id} value={v.vehicle_id}>
                      {v.rego} · {[v.make, v.model].filter(Boolean).join(" ") || v.vehicle_code}{v.allocation_label ? ` · ${v.allocation_label}` : ""}
                    </option>
                  ))}
                </Select>
              </label>
              {!vehicleId && (
                <label className="block text-[12px] text-text-muted">Not in the list? Type the rego and what it is
                  <Input value={vehicleText} onChange={e => setVehicleText(e.target.value)}/>
                </label>
              )}
              <label className="block text-[12px] text-text-muted">Driver <span className="text-danger">*</span>
                <Select value={driverEmployeeId} onChange={e => { setDriverEmployeeId(e.target.value); setDriverName(crew.find(c => c.employee_id === e.target.value)?.full_name ?? ""); }}>
                  <option value="">Pick…</option>
                  {crew.map(c => <option key={c.employee_id} value={c.employee_id}>{c.full_name}</option>)}
                </Select>
              </label>
              <div>
                <div className="text-[11px] text-text-muted mb-1.5">What was our vehicle doing at the moment of impact? <span className="text-danger">*</span></div>
                <div className="flex flex-wrap gap-1.5">
                  {MOVEMENTS.map(([k, l]) => (
                    <button key={k} type="button" onClick={() => setMovement(k)}
                      className={`text-[11px] px-2.5 py-1.5 rounded border transition ${movement === k ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <label className="block text-[12px] text-text-muted">Damage to our vehicle
                <Textarea rows={2} value={vehDamage} onChange={e => setVehDamage(e.target.value)} placeholder="Where on the vehicle, and how bad — e.g. bent number plate, scratches on the rear bar"/>
              </label>
              <YesNo label="Could it still be driven?" value={drivable} onChange={setDrivable} required/>
              <YesNo label="Was it towed?" value={towed} onChange={setTowed}/>
            </>
          )}

          {/* ---- Vehicle accident: Other party ---- */}
          {stepName === "Other party" && (
            <>
              <div className="text-[12px] text-text-muted">The other vehicle(s). Fill in what you got at the scene — a photo of their licence and rego on the next steps covers the rest.</div>
              {thirdParties.map((t, idx) => {
                const set = (patch: Partial<ThirdParty>) => setThirdParties(prev => prev.map((x, i) => i === idx ? { ...x, ...patch } : x));
                return (
                  <div key={idx} className="bg-ink rounded-md border border-line/50 p-3 space-y-2.5">
                    <div className="flex items-center justify-between">
                      <div className="text-[12px] font-medium">Other party {thirdParties.length > 1 ? idx + 1 : ""}</div>
                      {thirdParties.length > 1 && (
                        <button type="button" onClick={() => setThirdParties(prev => prev.filter((_, i) => i !== idx))} className="text-[11px] text-text-muted hover:text-danger inline-flex items-center gap-1"><X size={11}/> Remove</button>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="block text-[11px] text-text-muted col-span-2">Driver / rider name<Input value={t.name} onChange={e => set({ name: e.target.value })}/></label>
                      <label className="block text-[11px] text-text-muted">Phone<Input type="tel" value={t.phone} onChange={e => set({ phone: e.target.value })}/></label>
                      <label className="block text-[11px] text-text-muted">Email<Input type="email" value={t.email} onChange={e => set({ email: e.target.value })}/></label>
                      <label className="block text-[11px] text-text-muted col-span-2">Address<Input value={t.address} onChange={e => set({ address: e.target.value })}/></label>
                      <label className="block text-[11px] text-text-muted">Rego<Input value={t.rego} onChange={e => set({ rego: e.target.value })} className="uppercase"/></label>
                      <label className="block text-[11px] text-text-muted">Vehicle (make / model / type)<Input value={t.vehicle} onChange={e => set({ vehicle: e.target.value })} placeholder="e.g. Honda motorcycle"/></label>
                      <label className="block text-[11px] text-text-muted">Their insurer<Input value={t.insurer} onChange={e => set({ insurer: e.target.value })} placeholder="or 'none' / 'not stated'"/></label>
                      <label className="block text-[11px] text-text-muted">Policy / claim ref (if given)<Input value={t.policy_ref} onChange={e => set({ policy_ref: e.target.value })}/></label>
                    </div>
                    <label className="block text-[11px] text-text-muted">Damage to their vehicle
                      <Textarea rows={2} value={t.damage} onChange={e => set({ damage: e.target.value })} placeholder="What you could see"/>
                    </label>
                    <YesNo label="Was their vehicle towed?" value={t.towed} onChange={v => set({ towed: v })}/>
                    <YesNo label="Did they say they were hurt?" value={t.injury_reported} onChange={v => set({ injury_reported: v })}/>
                    {t.injury_reported && (
                      <label className="block text-[11px] text-text-muted">What they said about it
                        <Input value={t.injury_detail} onChange={e => set({ injury_detail: e.target.value })}/>
                      </label>
                    )}
                  </div>
                );
              })}
              <Button size="sm" variant="secondary" icon={Plus} onClick={() => setThirdParties(prev => [...prev, emptyThirdParty()])}>Another vehicle</Button>
            </>
          )}

          {/* ---- Vehicle accident: Conditions ---- */}
          {stepName === "Conditions" && (
            <>
              <label className="block text-[12px] text-text-muted">Where it happened <span className="text-danger">*</span>
                <Input value={locationText} onChange={e => setLocationText(e.target.value)} placeholder="Street / intersection and suburb"/>
              </label>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="secondary" icon={LocateFixed} disabled={!gps.fix}
                  onClick={() => gps.fix && setLocationFix({ lat: gps.fix.latitude, lng: gps.fix.longitude })}>
                  I'm at the scene now — pin my GPS
                </Button>
                {locationFix && <span className="text-[11px] text-ok">GPS pinned ✓ <button type="button" className="text-text-muted underline ml-1" onClick={() => setLocationFix(null)}>clear</button></span>}
                {!gps.fix && <span className="text-[11px] text-text-muted">(no GPS fix yet)</span>}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label className="block text-[11px] text-text-muted">Road
                  <Select value={cond.road} onChange={e => setCond(c => ({ ...c, road: e.target.value }))}><option value="">Pick…</option>{ROADS.map(r => <option key={r}>{r}</option>)}</Select>
                </label>
                <label className="block text-[11px] text-text-muted">Weather
                  <Select value={cond.weather} onChange={e => setCond(c => ({ ...c, weather: e.target.value }))}><option value="">Pick…</option>{WEATHERS.map(r => <option key={r}>{r}</option>)}</Select>
                </label>
                <label className="block text-[11px] text-text-muted">Light
                  <Select value={cond.light} onChange={e => setCond(c => ({ ...c, light: e.target.value }))}><option value="">Pick…</option>{LIGHTS.map(r => <option key={r}>{r}</option>)}</Select>
                </label>
                <label className="block text-[11px] text-text-muted">Speed zone
                  <Input inputMode="numeric" value={cond.speed_zone} onChange={e => setCond(c => ({ ...c, speed_zone: e.target.value }))} placeholder="km/h"/>
                </label>
              </div>
              <label className="block text-[12px] text-text-muted">Lanes — which lane each vehicle was in
                <Input value={cond.lane_detail} onChange={e => setCond(c => ({ ...c, lane_detail: e.target.value }))} placeholder="e.g. we were stopped in the right-turn lane; bike in the right lane behind"/>
              </label>
              <label className="block text-[12px] text-text-muted">Indicators / lights (what was on)
                <Input value={cond.indicators} onChange={e => setCond(c => ({ ...c, indicators: e.target.value }))} placeholder="e.g. right indicator on"/>
              </label>
              <label className="block text-[12px] text-text-muted">What did the other party say? (their words)
                <Textarea rows={2} value={tpStatement} onChange={e => setTpStatement(e.target.value)}/>
              </label>
              <label className="block text-[12px] text-text-muted">Did anyone say anything about fault? Write down exactly what was said and by whom — don't add your own view.
                <Textarea rows={2} value={admissions} onChange={e => setAdmissions(e.target.value)}/>
              </label>
              <YesNo label="Did police attend?" value={police.attended} onChange={v => setPolice(p => ({ ...p, attended: v }))} required/>
              {police.attended && (
                <div className="grid grid-cols-2 gap-2">
                  <label className="block text-[11px] text-text-muted">Police event / report number<Input value={police.event_number} onChange={e => setPolice(p => ({ ...p, event_number: e.target.value }))}/></label>
                  <label className="block text-[11px] text-text-muted">Station / officer<Input value={police.station} onChange={e => setPolice(p => ({ ...p, station: e.target.value }))}/></label>
                </div>
              )}
              <label className="block text-[12px] text-text-muted">Ambulance / fire / tow — who came and when
                <Input value={police.emergency} onChange={e => setPolice(p => ({ ...p, emergency: e.target.value }))} placeholder="leave blank if nobody"/>
              </label>
            </>
          )}

          {/* ---- Vehicle accident: Sketch (required) ---- */}
          {stepName === "Sketch" && (
            <>
              <div className="text-[12px] text-text-muted">Draw it. Easiest: pick a <b>typical accident</b> and move the vehicles to where they really were. Or build the road (type, lanes, turn lane), then tap <b>Our vehicle</b> and tap the road, same for theirs, an arrow for the way each was going, Impact where it hit, Area over the debris. Rotate a vehicle to point it the way it was facing. It saves when you tap Next.</div>
              <AccidentSketch items={sketchItems} onChange={items => { setSketchItems(items); setSketchDirty(true); }}
                road={sketchRoad} onRoad={r => { setSketchRoad(r); setSketchDirty(true); }}
                gps={gps.fix ? { latitude: gps.fix.latitude, longitude: gps.fix.longitude } : null} handleRef={sketchRef}/>
              {sketchPath && !sketchDirty && <div className="text-[11px] text-ok">Sketch saved ✓</div>}
            </>
          )}

          {/* ---- Vehicle accident: Sequence of events ---- */}
          {stepName === "Sequence of events" && (
            <>
              <div className="text-[12px] text-text-muted">One line per thing that happened, in order, with your best time. These go straight onto the incident's chain of events.</div>
              <div className="space-y-2">
                {timeline.map((row, idx) => (
                  <div key={idx} className="flex gap-2 items-start">
                    <Input type="time" className="w-[110px] shrink-0" value={row.time} onChange={e => setTimeline(prev => prev.map((r, i) => i === idx ? { ...r, time: e.target.value } : r))}/>
                    <Input className="flex-1" value={row.entry} placeholder={idx === 0 ? "e.g. Changed from the left lane into the right lane" : "then…"}
                      onChange={e => setTimeline(prev => prev.map((r, i) => i === idx ? { ...r, entry: e.target.value } : r))}/>
                    {timeline.length > 1 && (
                      <button type="button" onClick={() => setTimeline(prev => prev.filter((_, i) => i !== idx))} className="text-text-muted hover:text-danger p-2 shrink-0"><X size={14}/></button>
                    )}
                  </div>
                ))}
              </div>
              <Button size="sm" variant="secondary" icon={Plus}
                onClick={() => setTimeline(prev => [...prev, { time: prev[prev.length - 1]?.time ?? occTime, entry: "" }])}>
                Add a line
              </Button>
            </>
          )}

          {/* ---- Step: People ---- */}
          {stepName === "Who was involved" && (
            <>
              {isVehicle && (
                <label className="flex items-center gap-2 text-[13px] text-text bg-ink rounded-md border border-line/50 p-3">
                  <input type="checkbox" checked={anyoneHurt} onChange={e => setAnyoneHurt(e.target.checked)}/>
                  Someone was hurt (ours or theirs) — adds an injury step
                </label>
              )}
              <PeoplePicker
                label={isInjuryLike ? "Injured / affected people" : "People involved"}
                required={isInjuryLike}
                people={persons}
                onChange={setPersons}
              />
              <PeoplePicker
                label="Witnesses"
                people={witnesses}
                onChange={setWitnesses}
              />
            </>
          )}

          {/* ---- Step: Injuries ---- */}
          {stepName === "Injuries" && (
            <>
              {persons.length === 0 && (
                <div className="text-[12px] text-text-muted italic">Go back and add the injured person first.</div>
              )}
              {persons.map((p, idx) => (
                <div key={idx} className="bg-ink rounded-md border border-line/50 p-3 space-y-3">
                  <div className="text-[13px] font-medium">{p.name} {p.company && <span className="text-text-muted font-normal">· {p.company}</span>}</div>
                  <div>
                    <div className="text-[11px] text-text-muted mb-1.5">Where it hurts — circle it</div>
                    {/* 1 Nov 2026 — the body outline, red pen (James: "happy with red pen circle the areas"); the chips below are the words */}
                    <BodySketch strokes={p.injury?.sketch_strokes ?? []} title={`${p.name || "Injured person"} — where it hurts`}
                      handleRef={{ get current() { return bodyRefs.current[idx] ?? null; }, set current(h) { bodyRefs.current[idx] = h; } }}
                      onChange={st => setPersons(prev => prev.map((q, qi) => {
                        if (qi !== idx) return q;
                        const cur = q.injury ?? { body_parts: [], nature: "", mechanism: "", treatment: "" };
                        return { ...q, injury: { ...cur, sketch_strokes: st } };
                      }))}/>
                    <div className="text-[11px] text-text-muted mt-2 mb-1.5">Body part(s) — the words for the register</div>
                    <div className="flex flex-wrap gap-1.5">
                      {BODY_PARTS.map(bp => {
                        const on = p.injury?.body_parts?.includes(bp) ?? false;
                        return (
                          <button key={bp} type="button"
                            onClick={() => setPersons(prev => prev.map((q, qi) => {
                              if (qi !== idx) return q;
                              const cur = q.injury ?? { body_parts: [], nature: "", mechanism: "", treatment: "" };
                              const bps = on ? cur.body_parts.filter(b => b !== bp) : [...cur.body_parts, bp];
                              return { ...q, injury: { ...cur, body_parts: bps } };
                            }))}
                            className={`text-[11px] px-2 py-1 rounded-full border transition ${on ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>
                            {bp}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <label className="block text-[11px] text-text-muted">Nature of injury
                      <Select value={p.injury?.nature ?? ""} onChange={e => setPersons(prev => prev.map((q, qi) => qi === idx ? { ...q, injury: { ...(q.injury ?? { body_parts: [], mechanism: "", treatment: "" }), nature: e.target.value } as any } : q))}>
                        <option value="">Pick…</option>
                        {NATURES.map(n => <option key={n} value={n}>{n}</option>)}
                      </Select>
                    </label>
                    <label className="block text-[11px] text-text-muted">How it happened
                      <Select value={p.injury?.mechanism ?? ""} onChange={e => setPersons(prev => prev.map((q, qi) => qi === idx ? { ...q, injury: { ...(q.injury ?? { body_parts: [], nature: "", treatment: "" }), mechanism: e.target.value } as any } : q))}>
                        <option value="">Pick…</option>
                        {MECHANISMS.map(m => <option key={m} value={m}>{m}</option>)}
                      </Select>
                    </label>
                  </div>
                  <div>
                    <div className="text-[11px] text-text-muted mb-1.5">Treatment <span className="text-danger">*</span></div>
                    <div className="flex flex-wrap gap-1.5">
                      {TREATMENTS.map(t => {
                        const on = p.injury?.treatment === t.key;
                        return (
                          <button key={t.key} type="button"
                            onClick={() => setPersons(prev => prev.map((q, qi) => qi === idx ? { ...q, injury: { ...(q.injury ?? { body_parts: [], nature: "", mechanism: "" }), treatment: t.key } as any } : q))}
                            className={`text-[11px] px-2.5 py-1.5 rounded border transition ${on ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>
                            {t.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
              <div className="space-y-2">
                <label className="flex items-center gap-2 text-[12px] text-text">
                  <input type="checkbox" checked={lostTime} onChange={e => setLostTime(e.target.checked)}/>
                  Time off work expected (lost time)
                </label>
                <label className="block text-[12px] text-text-muted">Escalation
                  <Select value={escalation} onChange={e => setEscalation(e.target.value)}>
                    <option value="none">Not life-threatening</option>
                    <option value="life_threatening">Life-threatening — immediately notifiable</option>
                    <option value="fatality">Fatality</option>
                  </Select>
                </label>
              </div>
            </>
          )}

          {/* ---- Step: Damage details (environmental / property) ---- */}
          {stepName === "Damage details" && type === "environmental" && (
            <>
              <label className="block text-[12px] text-text-muted">Kind
                <Select value={envDetail.kind} onChange={e => setEnvDetail(v => ({ ...v, kind: e.target.value }))}>
                  <option value="spill">Spill (fuel / oil / hydraulic / chemical)</option>
                  <option value="air">Air (dust / odour / smoke)</option>
                  <option value="noise">Noise nuisance</option>
                  <option value="other">Other</option>
                </Select>
              </label>
              <label className="block text-[12px] text-text-muted">What / how much
                <Input value={envDetail.amount} onChange={e => setEnvDetail(v => ({ ...v, amount: e.target.value }))} placeholder="e.g. ~20L hydraulic oil"/>
              </label>
              <label className="block text-[12px] text-text-muted">Where it went
                <Select value={envDetail.surface} onChange={e => setEnvDetail(v => ({ ...v, surface: e.target.value }))}>
                  <option value="">Pick…</option>
                  <option>Sealed surface</option>
                  <option>Unsealed ground</option>
                  <option>Drain / waterway</option>
                  <option>Contained (bund / tray)</option>
                </Select>
              </label>
              <label className="block text-[12px] text-text-muted">Detail
                <Textarea rows={2} value={envDetail.detail} onChange={e => setEnvDetail(v => ({ ...v, detail: e.target.value }))}/>
              </label>
            </>
          )}
          {stepName === "Damage details" && type === "property_damage" && (
            <>
              <label className="block text-[12px] text-text-muted">Coretel property damaged
                <Textarea rows={2} value={propDetail.coretel} onChange={e => setPropDetail(v => ({ ...v, coretel: e.target.value }))} placeholder="What + rough extent (leave blank if none)"/>
              </label>
              <label className="block text-[12px] text-text-muted">Other party's property damaged
                <Textarea rows={2} value={propDetail.third_party} onChange={e => setPropDetail(v => ({ ...v, third_party: e.target.value }))} placeholder="What + whose + contact if known"/>
              </label>
            </>
          )}

          {/* ---- Step: Actions & evidence ---- */}
          {stepName === "Actions & evidence" && (
            <>
              <label className="block text-[12px] text-text-muted">What was done immediately <span className="text-danger">*</span>
                <Textarea rows={3} value={immediate} onChange={e => setImmediate(e.target.value)} placeholder="First aid given, area isolated, plant shut down, supervisor called…"/>
              </label>
              <label className="block text-[12px] text-text-muted">Did an authority issue a notice?
                <Select value={authorityNotice} onChange={e => setAuthorityNotice(e.target.value)}>
                  <option>None</option>
                  <option>Improvement notice</option>
                  <option>Prohibition notice</option>
                  <option>Infringement</option>
                  <option>Other</option>
                </Select>
              </label>

              <div>
                <div className="text-[12px] text-text-muted mb-1.5">Photos {photos.length === 0 && !noPhotosOk && <span className="text-danger">*</span>}</div>
                {isVehicle && (
                  <div className="mb-2">
                    <div className="text-[11px] text-text-muted mb-1">What the next photos show — pick, then add them. Our vehicle, theirs, their licence and rego, the road.</div>
                    <div className="flex flex-wrap gap-1.5">
                      {VEHICLE_PHOTO_TAGS.map(([k, l]) => (
                        <button key={k} type="button" onClick={() => setPhotoTag(k)}
                          className={`text-[11px] px-2.5 py-1.5 rounded border transition ${photoTag === k ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                )}
                <input ref={photoInputRef} type="file" accept="image/*" multiple hidden
                  onChange={e => handlePhotoFiles(e.target.files)} disabled={photoBusy}/>
                <div className="flex items-center gap-2 flex-wrap">
                  <Button size="sm" variant="secondary" icon={photoBusy ? Loader2 : Camera}
                    onClick={() => photoInputRef.current?.click()} disabled={photoBusy}>
                    {photoBusy ? "Uploading…" : "Add photos"}
                  </Button>
                  <span className="text-[11px] text-text-muted">{photos.length} added{photos.some(p => p.queued_offline) ? " (some queued for upload)" : ""}</span>
                </div>
                {photoErr && <div className="text-[11px] text-danger mt-1">{photoErr}</div>}
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {photos.map((p, i) => (
                      <div key={p.path} className="relative">
                        {p.preview_url
                          ? <img src={p.preview_url} alt="" className="w-16 h-16 object-cover rounded border border-line"/>
                          : <div className="w-16 h-16 rounded border border-line bg-ink flex items-center justify-center text-[10px] text-text-muted">photo</div>}
                        {p.queued_offline && <span className="absolute bottom-0.5 left-0.5 text-[9px] bg-warn/80 text-black px-1 rounded">queued</span>}
                        <button type="button" onClick={() => setPhotos(prev => prev.filter((_, pi) => pi !== i))}
                          className="absolute -top-1.5 -right-1.5 bg-ink-raised border border-line rounded-full p-0.5 text-text-muted hover:text-danger">
                          <X size={10}/>
                        </button>
                        {isVehicle && (
                          <select value={(p as any).tag ?? "other"} aria-label="What this photo shows"
                            onChange={e => setPhotos(prev => prev.map((q, qi) => qi === i ? { ...q, tag: e.target.value } as any : q))}
                            className="block w-16 mt-1 text-[9px] bg-ink border border-line rounded px-0.5 py-0.5 text-text-muted">
                            {VEHICLE_PHOTO_TAGS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {photos.length === 0 && (
                  <label className="flex items-center gap-2 text-[12px] text-text-muted mt-2">
                    <input type="checkbox" checked={noPhotosOk} onChange={e => setNoPhotosOk(e.target.checked)}/>
                    No photos available (say why in the description)
                  </label>
                )}
              </div>

              <div>
                <div className="text-[12px] text-text-muted mb-1.5">Your signature (optional but recommended)</div>
                <div className="border border-line rounded-md overflow-hidden bg-white">
                  <SignaturePad ref={sigRef} width={440} height={140}/>
                </div>
                <button type="button" onClick={() => sigRef.current?.clear()} className="text-[11px] text-text-muted hover:text-accent mt-1">Clear</button>
              </div>
            </>
          )}

          {/* ---- Step: Review ---- */}
          {stepName === "Review & submit" && type && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <Pill label={TYPE_TILES.find(t => t.key === type)?.label ?? type} tone="notice"/>
                <Pill label={severityLabel} tone={likelyNotifiable ? "critical" : SEV_RANK.indexOf(severityLabel) >= SEV_RANK.indexOf("Medical treatment") ? "warning" : severityLabel === "Report only" ? "default" : "pending"}/>
              </div>
              {likelyNotifiable && (
                <div className="bg-danger/10 border border-danger/30 rounded-md p-3 text-[12px] text-danger">
                  <AlertTriangle size={13} className="inline mr-1.5 -mt-0.5"/>
                  This looks <b>notifiable</b>. Directors are alerted the moment you submit, with the regulator and deadline on the register. Don't disturb the scene beyond making it safe.
                </div>
              )}
              <div className="space-y-1.5 text-[12px]">
                <Rv k="When" v={`${occDate} ${occTime}`}/>
                <Rv k="Where" v={project ? `${project.project_code} · ${project.sites?.site_name ?? project.project_name}` : "Not on a project site"}/>
                <Rv k="What" v={shortDesc}/>
                {isVehicle && (
                  <>
                    <Rv k="Our vehicle" v={(() => { const v = vehicles.find(x => x.vehicle_id === vehicleId); return v ? `${v.rego} · ${[v.make, v.model].filter(Boolean).join(" ")}` : vehicleText; })()}/>
                    <Rv k="Driver" v={driverName}/>
                    <Rv k="Doing" v={`${MOVEMENTS.find(m => m[0] === movement)?.[1] ?? "—"} · ${drivable ? "drivable" : drivable === false ? "not drivable" : "—"}${towed ? " · towed" : ""}`}/>
                    <Rv k="Other party" v={thirdParties.filter(t => t.name.trim() || t.rego.trim() || t.vehicle.trim()).map(t => [t.name, t.rego, t.vehicle].filter(s => s.trim()).join(" · ")).join("; ")}/>
                    <Rv k="Location" v={`${locationText}${locationFix ? " (GPS pinned)" : ""}`}/>
                    <Rv k="Police" v={police.attended ? `Attended${police.event_number ? ` · ${police.event_number}` : ""}` : police.attended === false ? "Did not attend" : "—"}/>
                    <Rv k="Sketch" v={sketchItems.length ? "drawn ✓" : "missing"}/>
                    <Rv k="Sequence" v={`${timeline.filter(t => t.entry.trim()).length} lines`}/>
                  </>
                )}
                <Rv k="People" v={persons.length ? persons.map(p => p.name).join(", ") : "—"}/>
                <Rv k="Witnesses" v={witnesses.length ? witnesses.map(p => p.name).join(", ") : "—"}/>
                <Rv k="Immediate actions" v={immediate}/>
                <Rv k="Photos" v={photos.length ? `${photos.length} attached` : "none (confirmed)"}/>
              </div>
              <div className="text-[11px] text-text-muted">
                Submitting records GPS, time and your identity, notifies every director, and opens the incident on the register for investigation.
              </div>
            </>
          )}

          {err && (
            <div className="bg-danger/10 border border-danger/20 rounded px-3 py-2 text-[12px] text-danger">{err}</div>
          )}
        </div>
      </Card>

      <StickyBottomBar className="p-3 flex justify-center">
        <div className="flex gap-2 max-w-lg w-full">
          <Button variant="secondary" size="lg" onClick={() => { setErr(null); step === 0 ? navigate(returnTo ?? "/forms") : setStep(s => s - 1); }}>
            {step === 0 ? "Cancel" : "Back"}
          </Button>
          {step < steps.length - 1 ? (
            <Button size="lg" icon={sketchBusy ? Loader2 : ArrowRight} onClick={next} disabled={sketchBusy} className="flex-1 justify-center">{sketchBusy ? "Saving sketch…" : "Next"}</Button>
          ) : (
            <Button size="lg" icon={submitMut.isPending ? undefined : CheckCircle2}
              onClick={() => submitMut.mutate()} disabled={submitMut.isPending}
              className="flex-1 justify-center">
              {submitMut.isPending ? <Loader2 size={14} className="animate-spin" /> : "Report incident"}
            </Button>
          )}
        </div>
      </StickyBottomBar>

      {submitMut.isError && submitMut.error?.message !== "validation_failed" && (
        <div className="mt-3 bg-danger/10 border border-danger/20 rounded px-3 py-2 text-[12px] text-danger">
          {String(submitMut.error?.message ?? submitMut.error)}
        </div>
      )}
    </div>
  );
}

// Yes / No as two big taps — a phone form, not a dropdown.
function YesNo({ label, value, onChange, required = false }: { label: string; value: boolean | null; onChange: (v: boolean) => void; required?: boolean }) {
  return (
    <div>
      <div className="text-[11px] text-text-muted mb-1.5">{label} {required && <span className="text-danger">*</span>}</div>
      <div className="flex gap-1.5">
        {([true, false] as const).map(v => (
          <button key={String(v)} type="button" onClick={() => onChange(v)}
            className={`text-[12px] px-4 py-1.5 rounded border transition ${value === v ? "bg-accent/15 border-accent text-accent" : "border-line text-text-muted hover:text-text"}`}>
            {v ? "Yes" : "No"}
          </button>
        ))}
      </div>
    </div>
  );
}

function Rv({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-2">
      <span className="text-text-muted w-[130px] flex-shrink-0">{k}</span>
      <span className="text-text min-w-0">{v || "—"}</span>
    </div>
  );
}

// =============================================================================
// PeoplePicker — employees + subbie workers + free-text visitors
// =============================================================================
function PeoplePicker({ label, people, onChange, required = false }: {
  label: string;
  people: Person[];
  onChange: (p: Person[]) => void;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [otherName, setOtherName] = useState("");
  const [otherCompany, setOtherCompany] = useState("");

  const { data: employees = [] } = useQuery({
    queryKey: ["picker_employees"],
    queryFn: async () => {
      const { data } = await supabase.from("v_employee_names")
        .select("employee_id, full_name").eq("is_active", true).order("full_name");
      return (data ?? []) as { employee_id: string; full_name: string }[];
    },
  });
  const { data: subbies = [] } = useQuery({
    queryKey: ["picker_subbie_workers"],
    queryFn: async () => {
      const { data } = await supabase.from("subbie_workers")
        .select("worker_id, first_name, last_name, company:subcontractor_company_id(trading_name, legal_name)")
        .eq("is_active", true).order("last_name");
      return (data ?? []) as any[];
    },
  });

  const chosen = new Set(people.map(p => p.name));
  const ql = q.trim().toLowerCase();
  const empHits = employees.filter(e => !chosen.has(e.full_name) && (!ql || e.full_name.toLowerCase().includes(ql))).slice(0, 6);
  const subHits = subbies.filter(w => {
    const name = `${w.first_name} ${w.last_name}`;
    return !chosen.has(name) && (!ql || name.toLowerCase().includes(ql));
  }).slice(0, 6);

  function add(p: Person) { onChange([...people, p]); setQ(""); }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-[12px] text-text-muted">{label} {required && <span className="text-danger">*</span>}</div>
        {!open && <Button size="sm" variant="secondary" icon={Plus} onClick={() => setOpen(true)}>Add</Button>}
      </div>

      {people.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {people.map((p, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 bg-ink border border-line/60 rounded-full pl-2.5 pr-1.5 py-1 text-[12px]">
              {p.name}
              {p.company && <span className="text-text-muted text-[11px]">· {p.company}</span>}
              <button type="button" onClick={() => onChange(people.filter((_, pi) => pi !== i))}
                className="text-text-muted hover:text-danger p-0.5"><X size={11}/></button>
            </span>
          ))}
        </div>
      )}
      {people.length === 0 && !open && (
        <div className="text-[12px] text-text-dim italic">Nobody added yet.</div>
      )}

      {open && (
        <div className="bg-ink-raised/40 border border-dashed border-line rounded-md p-3 space-y-2.5">
          <div className="relative">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted"/>
            <Input className="pl-8" placeholder="Search Coretel crew + subbie workers…" value={q} onChange={e => setQ(e.target.value)} autoFocus/>
          </div>
          {(empHits.length > 0 || subHits.length > 0) && (
            <div className="space-y-1 max-h-[180px] overflow-y-auto">
              {empHits.map(e => (
                <button key={e.employee_id} type="button"
                  onClick={() => add({ name: e.full_name, company: "Coretel", source: "employee", employee_id: e.employee_id })}
                  className="w-full text-left px-2.5 py-1.5 rounded hover:bg-ink text-[12px] flex items-center justify-between">
                  <span>{e.full_name}</span><span className="text-[10px] text-accent font-mono">CORETEL</span>
                </button>
              ))}
              {subHits.map(w => {
                const company = w.company?.trading_name ?? w.company?.legal_name ?? "Subbie";
                return (
                  <button key={w.worker_id} type="button"
                    onClick={() => add({ name: `${w.first_name} ${w.last_name}`, company, source: "subbie", worker_id: w.worker_id })}
                    className="w-full text-left px-2.5 py-1.5 rounded hover:bg-ink text-[12px] flex items-center justify-between">
                    <span>{w.first_name} {w.last_name}</span><span className="text-[10px] text-text-muted font-mono truncate max-w-[120px]">{company.toUpperCase()}</span>
                  </button>
                );
              })}
            </div>
          )}
          <div className="border-t border-line/40 pt-2.5 space-y-1.5">
            <div className="text-[11px] text-text-muted">Someone else (visitor, public, client)</div>
            <div className="flex gap-2 flex-wrap">
              <Input className="flex-1 min-w-[140px]" placeholder="Name" value={otherName} onChange={e => setOtherName(e.target.value)}/>
              <Input className="flex-1 min-w-[120px]" placeholder="Company / role" value={otherCompany} onChange={e => setOtherCompany(e.target.value)}/>
              <Button size="sm" disabled={!otherName.trim()}
                onClick={() => { add({ name: otherName.trim(), company: otherCompany.trim(), source: "other" }); setOtherName(""); setOtherCompany(""); }}>
                Add
              </Button>
            </div>
          </div>
          <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-text-muted hover:text-accent">Done</button>
        </div>
      )}
    </div>
  );
}
